import type { FC } from "react";
import { useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Table } from "@similarweb/pro-logic-components";

import { TableWrapper } from "app/pages/digital-marketing/generative-ai/shared/styledComponents";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
import { useSidePanelWrapper } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/hooks/useSidePanelWrapper";
import { SpacedTableContentContainer } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/styledComponents";
import { useSentimentOverviewData } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sentiment-report/hooks/useSentimentOverviewData";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import {
    getBaseWidgetRequestParams,
    getWidgetNoDataProps,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { requestParams } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";

import { COLUMNS_CONFIG } from "./config";
import { TopicPromptsSidePanel } from "./side-panel/TopicPromptsSidePanel";
import type { ITopicSentimentTableRow } from "./types";
import { transformData } from "./utils";

export const TopicsGraph: FC<ILLMReportWidget> = (props) => {
    const { campaign, campaignConfig, reportStatus, reportDuration } = props;
    const {
        params: { topic: queryTopic },
    } = useCurrentStateAndParams();

    const selectedTopic = (queryTopic as string) ?? ALL_TOPICS_NAME;
    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;
    const {
        isOpen: isSidePanelOpen,
        selectedRow,
        openSidePanel,
        closeSidePanel,
    } = useSidePanelWrapper<string>({ analyticsLabel: "Topics Graph Side Panel" });

    const apiParams = useMemo(
        () => getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
        [campaign, campaignConfig, reportDuration],
    );

    const {
        data,
        isLoading: isLoadingData,
        isError,
    } = useSentimentOverviewData(apiParams, {
        enabled: canLoadData,
    });

    const excelUrl =
        canLoadData && campaign?.id
            ? `/api/aiTracker/reports/sentimentAnalysis/Overview/Excel?${requestParams(apiParams)}`
            : "";

    const onExcelClick = (): void => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "Table/Sentiment by Topics/Excel");
    };

    const [activeRow, setActiveRow] = useState<ITopicSentimentTableRow | undefined>();
    const allTableData = useMemo<ITopicSentimentTableRow[]>(() => transformData(data?.topics), [data]);
    const tableData = useMemo<ITopicSentimentTableRow[]>(
        () =>
            selectedTopic === ALL_TOPICS_NAME
                ? allTableData
                : allTableData.filter((row) => row.topicName?.toLowerCase() === selectedTopic.toLowerCase()),
        [allTableData, selectedTopic],
    );
    const shouldShowNoData = isError || !tableData?.length || reportStatus === ReportStatus.Error;
    const shouldShowLoading = (isLoadingData || !campaign) && reportStatus !== ReportStatus.Error;

    return (
        <>
            <WidgetContainer
                {...getWidgetNoDataProps(shouldShowNoData, isError)}
                title={translate("llm.sentiment_report.topics.title")}
                tooltip={translate("llm.sentiment_report.topics.tooltip")}
                data-automation="topics-sentiment-widget"
                isLoading={shouldShowLoading}
                loaderWidth={"100%"}
                loaderHeight={400}
                withBorder
                excelUrl={excelUrl}
                onExcelClick={onExcelClick}
            >
                <TableWrapper>
                    <SpacedTableContentContainer
                        data-automation="topics-sentiment-content"
                        width="100%"
                        padding="16px 24px"
                    >
                        <Table
                            id="topics-sentiment-table"
                            size="large"
                            rowHoverable
                            sticky={false}
                            activeRowSelector={(row) => row.topicName === activeRow?.topicName}
                            onRow={(record) => ({
                                onClick: () => {
                                    setActiveRow(record);
                                    openSidePanel(record.topicName);
                                },
                                style: { cursor: "pointer" },
                            })}
                            columnConfig={COLUMNS_CONFIG}
                            data={tableData}
                            theme="white"
                            isLoading={shouldShowLoading}
                        />
                    </SpacedTableContentContainer>
                </TableWrapper>
            </WidgetContainer>
            <TopicPromptsSidePanel
                {...props}
                isOpen={isSidePanelOpen}
                topic={selectedRow}
                onClose={() => {
                    closeSidePanel();
                    setActiveRow(undefined);
                }}
            />
        </>
    );
};
